// ── Working Papers ────────────────────────────────────────────────────────
// The desk that stands in front of the Marked Pages shelf. Same designer,
// same material language as Ledger.js's SHELF_CSS, and the same law:
//
//   Every ground, rule and paper here is mixed from the app's own tokens
//   (--color-text / surface / background / primary / pho / success / error)
//   with color-mix, so it survives all eleven themes, light and dark. Only
//   the highlights and shadows are plain alpha white and black — a highlight
//   is a highlight in every headspace, and a shadow is always the absence of
//   light. There are no hardcoded palettes.
//
// Two objects live on the desk:
//   THE DAY BOOK — a bound ruled leaf (spine hinge left, page-block edge
//                  right, a margin rule down the sheet). Every action is one
//                  ruled entry; every project is a running head with a dotted
//                  leader and a plain count — the shelf's contents-page device.
//   THE BLOTTER  — a desk pad with the month printed on it, held at four
//                  leather corners, sheets stacked visibly beneath.
//
// WP_TOKENS is shared; each surface adds its own half. Both are injected in a
// <style> tag by their component (identical rules if both are mounted).

export const WP_TOKENS = `
.wp-desk{
  text-align:left;
  --wp-paper:color-mix(in srgb, var(--color-background) 55%, var(--color-surface));
  --wp-paper-edge:color-mix(in srgb, var(--wp-paper) 86%, var(--color-text));
  --wp-rule:color-mix(in srgb, var(--color-text) 15%, transparent);
  --wp-rule-soft:color-mix(in srgb, var(--color-text) 10%, transparent);
  --wp-slip:color-mix(in srgb, var(--color-background) 78%, var(--color-surface));
  --wp-slip-edge:color-mix(in srgb, var(--wp-slip) 88%, var(--color-text));
  --wp-felt-hi:color-mix(in srgb, var(--color-primary) 26%, color-mix(in srgb, var(--color-text) 62%, var(--color-background)));
  --wp-felt-lo:color-mix(in srgb, var(--color-primary) 16%, color-mix(in srgb, var(--color-text) 80%, var(--color-background)));
  /* the corners are leather cut from the desk, so they are mixed from the felt
     — not from the text — and stay related to it in every theme */
  --wp-corner-a:color-mix(in srgb, var(--color-primary) 52%, var(--wp-felt-hi));
  --wp-corner-b:color-mix(in srgb, var(--color-primary) 26%, var(--wp-felt-lo));
  --wp-gold:color-mix(in srgb, var(--color-primary) 55%, var(--color-surface));
  /* The inks. A theme's success/error/primary are UI colours, chosen to sit on
     a chip — as ink on paper at 9px several of them are unreadable (measured:
     2.1–3.0 in default, arctic and paper). Pulling each toward --color-text
     makes it a written ink instead of a highlight, and it moves the right way
     in both directions: darker on light paper, lighter on dark. */
  --wp-do:color-mix(in srgb, var(--color-success) 70%, var(--color-text));
  --wp-due:color-mix(in srgb, var(--color-error) 76%, var(--color-text));
  --wp-mot:color-mix(in srgb, var(--color-primary) 80%, var(--color-text));
  --wp-star:var(--color-pho);
  /* The small mono labels — running heads, counts, day numbers — are set at
     8.5–9px, where a theme's textMuted can fall to 2.6. Same treatment. */
  --wp-muted:color-mix(in srgb, var(--color-textMuted) 62%, var(--color-text));
  --wp-soft:color-mix(in srgb, var(--color-text) 70%, transparent);
  --wp-faint:color-mix(in srgb, var(--color-text) 50%, transparent);
  --wp-contact:rgba(0,0,0,.34);
  --wp-cast:rgba(0,0,0,.26);
  --wp-label:ui-monospace,"SF Mono",Menlo,Consolas,monospace;
  --wp-margin:58px;
}

/* the desk: leather under one lamp, above-left, finished with a gilt line */
.wp-desk{
  position:relative;border-radius:10px;
  background:
    repeating-linear-gradient(45deg, rgba(255,255,255,.020) 0 1px, rgba(255,255,255,0) 1px 3px),
    radial-gradient(1000px 340px at 18% -60px, rgba(255,255,255,.14), rgba(0,0,0,0) 72%),
    linear-gradient(180deg, var(--wp-felt-hi), var(--wp-felt-lo));
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.16),
    inset 0 -30px 40px -30px rgba(0,0,0,.45),
    0 26px 50px -28px var(--wp-cast);
}
.wp-desk::after{
  content:"";position:absolute;inset:9px;border:1px solid color-mix(in srgb, var(--wp-gold) 60%, transparent);
  border-radius:5px;pointer-events:none;z-index:0;
}
.wp-kick{font-family:var(--wp-label);font-size:9px;letter-spacing:.2em;text-transform:uppercase;color:var(--wp-muted)}
`;

// ── THE LIST ──────────────────────────────────────────────────────────────
// The Board's design language: one action per row on the app's own plain
// tokens. The desk, the leaf and the paper went back to the prop room — the
// List had drifted into a different theme from the rest of the app, and its
// masthead had turned into a second project card.
//
// The philosophy (Paul's, Aug 20 2026): Goals view is where a goal or a
// project shows its face — cards, plans, the whole picture tied together.
// List and Board are fast lanes. The left nav already names the goal and the
// project, so these surfaces carry no card, no charter, no masthead: they go
// straight to the action.
export const WP_LIST_CSS = `
/* a project's running head — dot, name, dotted leader, count */
.wp-sec{display:flex;align-items:center;gap:8px;padding:18px 16px 6px}
.wp-sec .ink{width:8px;height:8px;border-radius:999px;flex:none}
.wp-sec .ink.none{background:var(--color-border)}
.wp-sec .hd{display:flex;align-items:baseline;gap:8px;flex:1;min-width:0}
.wp-sec .nm{font-size:11px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--color-textMuted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.wp-sec .n{font-size:11px;color:var(--color-textMuted);white-space:nowrap;font-variant-numeric:tabular-nums}
.wp-lead{flex:1;min-width:12px;border-bottom:1px dotted var(--color-border)}

/* one action, one row */
/* Rows became cells Oct 7 2026 (the comp language): a quiet card with a 3px
   status edge — to-do wears the theme's accent, in-motion its warning — in
   place of the old lined-paper underline. Same markup, same behavior. */
.wp-ln{display:flex;align-items:center;gap:9px;margin:0 12px 8px;padding:9px 13px;
  background:var(--color-surface);
  border:1px solid var(--color-border);
  border-left:3px solid var(--color-primary);
  border-radius:6px;cursor:pointer;
  transition:background .12s,border-color .12s}
.wp-ln:hover{background:var(--color-surfaceHover)}
.wp-ln.sel{border-color:var(--color-primary);background:var(--color-surfaceHover)}
.wp-ln.doing{border-left-color:var(--color-warning)}
.wp-ln .wp-lead{border-bottom:0}
.wp-grip{flex:none;width:12px;text-align:center;font-size:10px;color:var(--color-textMuted);opacity:0;cursor:grab}
.wp-sheet[data-manual="1"] .wp-ln:hover .wp-grip{opacity:.55}
.wp-box{flex:none;width:15px;height:15px;border-radius:999px;border:1.5px solid var(--color-border);background:transparent;color:var(--color-background);display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0}
.wp-box:hover{border-color:var(--color-success)}
.wp-box.on{background:var(--color-success);border-color:var(--color-success)}
.wp-st{flex:none;background:none;border:0;padding:0 1px;font-size:12px;line-height:1;color:var(--color-border);cursor:pointer}
.wp-st:hover{color:var(--color-pho)}
.wp-ln.star .wp-st{color:var(--color-pho)}
.wp-txt{flex:1;min-width:0;display:flex;align-items:baseline;gap:8px;cursor:pointer}
.wp-tt{font-size:13px;line-height:1.5;color:var(--color-text);overflow-wrap:anywhere}
.wp-edit{width:100%;font-size:13px;color:var(--color-text);background:transparent;border:0;border-bottom:1px solid var(--color-primary);outline:none;padding:0 0 1px}
/* The stamps and dates yield before the title does: in a narrow column the
   right block clips instead of squeezing the words to one letter a line. */
.wp-right{display:flex;align-items:center;gap:10px;flex:0 1 auto;min-width:36px;overflow:hidden;justify-content:flex-end}
.wp-txt{flex:1 1 55%}
/* The cell's warning-colored left edge says "in motion" now — the written
   stamp became redundant ink and clipped badly in narrow columns (Oct 7 2026). */
.wp-stamp{display:none}
.wp-when{font-size:11px;color:var(--color-textMuted);font-variant-numeric:tabular-nums;white-space:nowrap}
.wp-when.od{color:var(--color-error);font-weight:600}
.wp-ln.flash{box-shadow:inset 0 0 0 2px color-mix(in srgb, var(--color-pho) 55%, transparent)}

/* nothing here yet */
.wp-blank{padding:56px 16px;text-align:center}
.wp-blank .g{font-size:14px;color:var(--color-textSecondary)}
.wp-blank .s{font-size:12px;color:var(--color-textMuted);margin-top:6px}

/* done today — scoped to the same filter as the rows above it */
.wp-tail{margin-top:14px;border-top:1px solid var(--color-borderLight)}
.wp-tailhd{display:flex;align-items:center;gap:8px;width:100%;text-align:left;background:none;border:0;cursor:pointer;padding:10px 16px;font-size:12px;font-weight:500;color:var(--color-textMuted)}
.wp-tailhd:hover{color:var(--color-text)}
.wp-tailhd .n{font-variant-numeric:tabular-nums}
.wp-tailhd .chev{transition:transform .15s}
.wp-tailhd[aria-expanded="true"] .chev{transform:rotate(90deg)}
.wp-dn{display:flex;align-items:flex-start;gap:10px;padding:8px 16px;border-bottom:1px solid var(--color-borderLight)}
.wp-dn .rb{flex:none;margin-top:2px;width:15px;height:15px;border-radius:999px;border:0;background:var(--color-success);color:var(--color-background);display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0}
.wp-dn .body{flex:1;min-width:0}
.wp-dn .t{font-size:13px;color:var(--color-textSecondary)}
.wp-dn .said{font-size:12.5px;font-style:italic;color:var(--color-text);margin-top:2px}
.wp-dn .end{flex:none;text-align:right}
.wp-dn .how{display:block;font-size:9.5px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--color-textMuted)}
.wp-dn .at{display:flex;align-items:center;justify-content:flex-end;font-size:11px;color:var(--color-textMuted);margin-top:2px}
.wp-dn .where{display:inline-flex;align-items:center}
.wp-dn .where i{display:inline-block;width:6px;height:6px;border-radius:999px;margin:0 4px 0 8px}
`;

// ── THE BLOTTER ───────────────────────────────────────────────────────────
export const WP_CAL_CSS = `
.wp-cal{padding:16px 18px 20px}
.wp-padwrap{position:relative;padding:10px 12px 14px}
.wp-pad{
  position:relative;border-radius:3px;padding:18px 20px 22px;
  background:linear-gradient(180deg, var(--wp-paper) 96%, var(--wp-paper-edge));
  box-shadow:
    0 1px 0 rgba(255,255,255,.30) inset,
    0 2px 0 -1px color-mix(in srgb, var(--wp-paper-edge) 86%, #000),
    0 4px 0 -2px color-mix(in srgb, var(--wp-paper-edge) 74%, #000),
    0 6px 0 -3px color-mix(in srgb, var(--wp-paper-edge) 62%, #000),
    0 4px 5px -2px var(--wp-contact),
    0 30px 46px -24px var(--wp-cast);
}
/* the pad's own page block at the foot — loose stock, so it dims with the room */
.wp-pad::after{
  content:"";position:absolute;left:3px;right:3px;bottom:-4px;height:4px;z-index:0;border-radius:0 0 2px 2px;
  background:repeating-linear-gradient(90deg,
    color-mix(in srgb, var(--wp-paper) 88%, #fff) 0 2px,
    color-mix(in srgb, var(--wp-paper-edge) 80%, #000) 2px 3px);
}
.wp-cnr{position:absolute;width:52px;height:52px;z-index:4;pointer-events:none}
.wp-cnr i{
  position:absolute;inset:0;display:block;
  background:linear-gradient(140deg, var(--wp-corner-a), var(--wp-corner-b));
  box-shadow:0 1px 3px rgba(0,0,0,.36);
}
.wp-cnr.tl{top:-2px;left:-2px}      .wp-cnr.tl i{clip-path:polygon(0 0,100% 0,0 100%)}
.wp-cnr.tr{top:-2px;right:-2px}     .wp-cnr.tr i{clip-path:polygon(100% 0,100% 100%,0 0)}
.wp-cnr.bl{bottom:-2px;left:-2px}   .wp-cnr.bl i{clip-path:polygon(0 0,0 100%,100% 100%)}
.wp-cnr.br{bottom:-2px;right:-2px}  .wp-cnr.br i{clip-path:polygon(100% 0,100% 100%,0 100%)}

.wp-cmast{display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap;padding:2px 0 10px;border-bottom:1px solid var(--wp-rule);position:relative;z-index:2}
.wp-caltitle{
  font-family:var(--font-display, Georgia, serif);font-weight:500;font-size:24px;line-height:1;
  letter-spacing:.005em;color:var(--color-text);
}
.wp-arrows{display:flex;gap:3px;padding-bottom:2px}
.wp-arrow{
  width:24px;height:24px;border:1px solid var(--wp-rule);border-radius:5px;background:transparent;cursor:pointer;
  color:var(--wp-muted);display:flex;align-items:center;justify-content:center;
}
.wp-arrow:hover{color:var(--color-text);border-color:var(--color-textMuted)}
.wp-arrow:focus-visible{outline:2px solid var(--color-primary);outline-offset:2px}
/* index tabs standing on the rule */
.wp-itabs{display:flex;align-items:flex-end;gap:3px;margin-left:2px}
.wp-itab{
  font:500 11px/1 var(--font-main);cursor:pointer;border:1px solid var(--wp-rule);border-bottom:0;
  border-radius:5px 5px 0 0;padding:7px 12px 8px;background:transparent;color:var(--wp-muted);
  position:relative;bottom:-11px;text-transform:capitalize;
}
.wp-itab:hover{color:var(--color-text)}
.wp-itab[aria-pressed="true"]{
  color:var(--color-text);font-weight:600;
  background:linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 12%, transparent), transparent);
  border-color:color-mix(in srgb, var(--color-primary) 40%, transparent);
  box-shadow:0 2px 0 0 var(--wp-paper);
}
.wp-itab:focus-visible{outline:2px solid var(--color-primary);outline-offset:2px}
/* the lens: one at a time */
.wp-lens{display:flex;border:1px solid var(--wp-rule);border-radius:6px;overflow:hidden;margin-bottom:2px}
.wp-lens button{
  display:flex;align-items:center;gap:6px;font:500 11px/1 var(--font-main);cursor:pointer;border:0;
  background:transparent;color:var(--wp-muted);padding:7px 11px;
}
.wp-lens button + button{border-left:1px solid var(--wp-rule)}
.wp-lens button[aria-pressed="true"]{background:color-mix(in srgb, var(--color-text) 9%, transparent);color:var(--color-text);font-weight:600}
.wp-lens button:focus-visible{outline:2px solid var(--color-primary);outline-offset:-3px}
.wp-lens i{width:7px;height:7px;border-radius:2px;display:inline-block}
.wp-calgap{flex:1}
.wp-say{font-family:var(--wp-label);font-size:9px;letter-spacing:.06em;color:var(--wp-muted);padding-bottom:3px;text-align:right}

/* the printed week ruling */
.wp-dow{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));padding:10px 0 6px}
.wp-dow span{font-family:var(--wp-label);font-size:8.5px;letter-spacing:.2em;text-transform:uppercase;color:var(--wp-muted);padding-left:7px}
.wp-grid{display:grid;border-top:1px solid var(--wp-rule);border-left:1px solid var(--wp-rule);border-radius:2px;overflow:hidden;position:relative;z-index:2}
.wp-grid.month,.wp-grid.week{grid-template-columns:repeat(7,minmax(0,1fr))}
.wp-grid.day{grid-template-columns:minmax(0,1fr);max-width:760px;margin:0 auto}
.wp-cell{border-right:1px solid var(--wp-rule);border-bottom:1px solid var(--wp-rule);padding:6px 6px 8px;min-width:0;position:relative}
.wp-grid.month .wp-cell{min-height:104px}
.wp-grid.week .wp-cell{min-height:320px}
.wp-grid.day .wp-cell{min-height:420px}
.wp-cell.we{background:color-mix(in srgb, var(--color-text) 3.5%, transparent)}
.wp-cell.out{opacity:.42}
.wp-cell .num{display:flex;align-items:center;gap:6px;margin-bottom:5px;min-height:19px}
.wp-cell .num b{font:400 11.5px/1 var(--wp-label);font-variant-numeric:tabular-nums;color:var(--wp-muted);padding:3px 4px}
.wp-cell.now .num b{color:var(--wp-due);font-weight:700;position:relative}
/* today, circled by hand */
.wp-cell.now .num b::after{
  content:"";position:absolute;left:-4px;right:-5px;top:-4px;bottom:-4px;
  border:1.5px solid var(--wp-due);
  border-radius:48% 52% 51% 49% / 52% 48% 52% 48%;
  transform:rotate(-5deg);opacity:.85;
}
.wp-cell .tdy{font:700 8px/1 var(--wp-label);letter-spacing:.18em;text-transform:uppercase;color:var(--wp-due)}
.wp-cell .mon{font:400 8px/1 var(--wp-label);letter-spacing:.18em;text-transform:uppercase;color:var(--wp-muted)}
.wp-cell .stack{display:flex;flex-direction:column;gap:2px}

/* an entry — a written line, not a widget */
.wp-ent{
  display:flex;align-items:baseline;gap:5px;width:100%;text-align:left;cursor:pointer;border:0;background:transparent;
  padding:2px 4px 2px 0;border-left:2px solid transparent;position:relative;
}
.wp-ent:hover{background:color-mix(in srgb, var(--color-text) 6%, transparent)}
.wp-ent:focus-visible{outline:2px solid var(--color-primary);outline-offset:1px}
/* what you chose sits on your own stock; what the world set is printed bare */
.wp-ent.k-do,.wp-ent.k-focus{
  border-left-color:var(--wp-do);padding-left:5px;border-radius:0 2px 2px 0;
  background:linear-gradient(180deg, var(--wp-slip), var(--wp-slip-edge));
  box-shadow:0 1px 1px var(--wp-contact);
}
.wp-ent.k-due{border-left-color:var(--wp-due);padding-left:5px}
.wp-ent.k-due.od{background:color-mix(in srgb, var(--wp-due) 10%, transparent)}
.wp-ent .pd{width:5px;height:5px;border-radius:999px;flex:none;align-self:center;box-shadow:0 0 0 1px rgba(0,0,0,.18)}
.wp-ent .t{
  flex:1;min-width:0;font-size:11.5px;line-height:1.34;color:var(--color-text);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
}
.wp-grid.week .wp-ent .t,.wp-grid.day .wp-ent .t{white-space:normal}
.wp-grid.day .wp-ent .t{font-size:13px}
.wp-ent .t i{font-style:normal;color:var(--wp-star);padding-right:2px}
.wp-ent .ns{display:block;margin-top:2px;font-family:var(--font-display, Georgia, serif);font-style:italic;font-size:11.5px;color:var(--wp-muted)}
.wp-ent .tag{font:700 7.5px/1 var(--wp-label);letter-spacing:.05em;text-transform:uppercase;flex:none;align-self:center}
/* with room to breathe, the tag drops to its own line instead of squeezing the words */
.wp-grid.week .wp-ent,.wp-grid.day .wp-ent{flex-wrap:wrap}
.wp-grid.week .wp-ent .tag,.wp-grid.day .wp-ent .tag{font-size:8px;letter-spacing:.13em;flex:0 0 100%;order:3;margin-top:3px;padding-left:10px}
.wp-ent .tag.due{color:var(--wp-due)}
.wp-ent .tag.ok{color:var(--wp-do)}
.wp-ent .tag.plain{color:var(--wp-muted);font-weight:400;letter-spacing:.06em}
.wp-calblank{padding:70px 24px 76px;text-align:center;position:relative;z-index:2}
.wp-calblank .g{font-family:var(--font-display, Georgia, serif);font-size:18px;color:var(--color-text)}
.wp-calblank .s{margin:9px auto 0;max-width:430px;font-size:12.5px;line-height:1.65;color:var(--wp-muted)}
.wp-calblank .s b{font-weight:600}

@keyframes wp-pulsecell{0%{background:color-mix(in srgb, var(--wp-due) 18%, transparent)}100%{background:transparent}}
.wp-cell.pulse{animation:wp-pulsecell .9s ease-out}

@media (max-width:900px){
  .wp-grid.month .wp-cell{min-height:92px;padding:5px 4px 6px}
  .wp-grid.month .wp-ent{gap:4px;padding-right:2px}
  .wp-grid.month .wp-ent .t{font-size:10.5px}
  .wp-grid.month .wp-ent .tag{font-size:7px;letter-spacing:0}
  .wp-pad{padding:16px 12px 18px}
}
@media (max-width:720px){
  .wp-cal{padding:8px}
  .wp-grid.month .wp-cell{min-height:72px;padding:4px 3px}
  .wp-ent .t{font-size:10px}
  .wp-say{display:none}
}
@media (prefers-reduced-motion: reduce){.wp-cell.pulse{animation:none}}
`;
